/**
 * Display helpers for "How was it?", the day-after rating
 * (documents/post-event-question-CONTRACT.md).
 *
 * The day after an event the people who came are asked to rate it from 1 to 5
 * and say what could have been better. Two server-written collections feed the
 * event page's Reviews section:
 *
 *  - eventReviewSummaries/{eventId}: what hosts see. Never a name. Until 3 or
 *    more guests have rated (`visible`), it holds only the counts: the server
 *    does not write the average, spread or comments at all, because a host
 *    could read them straight from the doc even if the panel hid them.
 *  - eventReviews/{eventId}_{uid}: one per guest asked, with their rating and
 *    every comment they sent. Admin operators only; the rules refuse hosts,
 *    since every doc names who wrote it.
 */

import { eventAccessFor } from "./eventHosts";

/**
 * Whether the event page shows its Reviews section: a finished, not cancelled
 * event, to the host, a co-host or an admin. Reviews are not money, so co-hosts
 * count (canSeeEvent, not the primary-only checks). `isSuperAdmin` is the claim
 * OR the vendors flag, the same test as the rules' isAdminOperator().
 */
export const showsReviews = (event, { uid, isSuperAdmin = false } = {}) =>
  !!event &&
  event.isCompleted === true &&
  event.status !== "cancelled" &&
  eventAccessFor(event, { uid, isSuperAdmin }).canSeeEvent;

/** A refused read or call, which the section treats as "nothing to show", not an error. */
export const isPermissionDenied = (err) =>
  err?.code === "permission-denied" || err?.code === "functions/permission-denied";

/**
 * Where the host's summary stands: "none" when there is no summary doc (nobody
 * was asked: the send writes one with count 0 once guests are asked), "hidden"
 * while fewer than 3 have rated, "visible" once the average, spread and
 * comments are there to show.
 */
export const reviewSummaryState = (summary) => {
  if (!summary || typeof summary !== "object") return "none";
  return summary.visible === true ? "visible" : "hidden";
};

/** Ratings from 1 to 5. A 0 ("I didn't make it") is counted apart, in didNotGo. */
export const reviewCount = (summary) => Math.max(0, Math.floor(Number(summary?.count) || 0));

export const NOT_ASKED_TEXT = "Guests were not asked about this event.";

/** "Not enough reviews yet (N so far)." */
export const notEnoughReviewsText = (summary) => `Not enough reviews yet (${reviewCount(summary)} so far).`;

/** The average to one decimal ("4.6"), or null when the doc has none. */
export const averageText = (summary) => {
  const average = Number(summary?.average);
  return Number.isFinite(average) && average > 0 ? average.toFixed(1) : null;
};

/** The spread as rows from 5 down to 1, each with its count and its share of all ratings. */
export const distributionRows = (summary) => {
  const spread = summary?.distribution || {};
  const rows = [5, 4, 3, 2, 1].map((rating) => ({
    rating,
    count: Math.max(0, Math.floor(Number(spread[String(rating)]) || 0)),
  }));
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  return rows.map((row) => ({ ...row, share: total > 0 ? row.count / total : 0 }));
};

/** The comments a host may read, text only, in the order the server wrote them (by random key). */
export const summaryComments = (summary) =>
  (Array.isArray(summary?.comments) ? summary.comments : [])
    .filter((comment) => typeof comment?.text === "string" && comment.text.trim())
    .map((comment, index) => ({ key: comment.key || `comment-${index}`, text: comment.text }));

// ---- The per-guest list (admin operators only) -----------------------------

/** One guest's rating in words: "4 / 5", "Didn't make it" for a 0, or "No rating yet". */
export const ratingText = (rating) => {
  if (rating === 0) return "Didn't make it";
  if (Number.isInteger(rating) && rating >= 1 && rating <= 5) return `${rating} / 5`;
  return "No rating yet";
};

// What the classifier made of a comment. Only "feedback" can reach hosts;
// "unclassified" means the classifier call failed and the server will retry it.
export const COMMENT_KINDS = {
  feedback: { label: "Feedback", tone: "green" },
  safety: { label: "Safety", tone: "red" },
  other: { label: "Other", tone: "blue" },
  unclassified: { label: "Unclassified", tone: "gray" },
};

export const commentKindOf = (entry) => (COMMENT_KINDS[entry?.kind] ? entry.kind : "unclassified");

/**
 * Whether a comment goes into the host's summary, as the server decides it:
 * "shown" for feedback nobody hid, "hidden" when an admin took it out, and
 * "adminOnly" for every other kind. Shown still waits on the 3-rater floor.
 */
export const commentHostVisibility = (entry) => {
  if (entry?.hiddenFromHost === true) return "hidden";
  return commentKindOf(entry) === "feedback" ? "shown" : "adminOnly";
};

export const COMMENT_VISIBILITY_TEXT = {
  shown: "Shown to hosts",
  hidden: "Hidden from hosts",
  adminOnly: "Admins only",
};

const AUDIENCE_TEXT = {
  checked_in: "Checked in",
  said_yes: "Said yes, didn't check in",
  said_maybe: "Said maybe, didn't check in",
  rsvp: "Signed up",
};

const ASKED_VIA_TEXT = {
  sms: "Asked by text",
  push: "Asked with a notification",
  none: "Not texted or notified",
};

/** Why and how a guest was asked, plus a failed text: "Checked in. Asked by text." */
export const reviewAskedText = (entry) => {
  if (!entry || typeof entry !== "object") return null;
  const parts = [];
  if (AUDIENCE_TEXT[entry.audience]) parts.push(`${AUDIENCE_TEXT[entry.audience]}.`);
  if (ASKED_VIA_TEXT[entry.askedVia]) parts.push(`${ASKED_VIA_TEXT[entry.askedVia]}.`);
  if (entry.smsFailedAt) {
    parts.push(entry.smsErrorCode ? `The text failed (error ${entry.smsErrorCode}).` : "The text failed.");
  }
  return parts.length > 0 ? parts.join(" ") : null;
};

const hasAnswered = (row) => row.rating !== null || row.comments.length > 0;

/**
 * One row per guest asked: their name from the event's guest list, rating and
 * comments. Guests who answered come first, then everyone by name.
 */
export const reviewGuestRows = (reviewsByUser, eventUsers) =>
  Object.values(reviewsByUser || {})
    .filter((review) => review && typeof review.userId === "string" && review.userId)
    .map((review) => ({
      uid: review.userId,
      name: eventUsers?.[review.userId]?.name || "Unknown guest",
      rating: Number.isInteger(review.rating) ? review.rating : null,
      comments: (Array.isArray(review.comments) ? review.comments : []).filter(
        (comment) => typeof comment?.text === "string"
      ),
      askedText: reviewAskedText(review),
    }))
    .sort((a, b) => {
      const answered = Number(hasAnswered(b)) - Number(hasAnswered(a));
      return answered !== 0 ? answered : a.name.localeCompare(b.name);
    });

/** "12 asked, 8 answered" for the top of the per-guest list. */
export const reviewGuestCountsText = (rows) => {
  const list = rows || [];
  const answered = list.filter(hasAnswered).length;
  return `${list.length} asked, ${answered} answered`;
};
